import { useMemo } from 'react'
import { toast } from 'react-toastify'
import { DashboardWidgetPreference, useSystemActions, useSystemState, useSystemStateActions, useSystemStateHooks } from '../../system'
import { DASHBOARD_COLUMNS, RoleDashboardWidget, widgetSizeGrid } from '../types'

// Default layout of a role: its default cards in order, filling each row from left to right.
// The cards it can only add start hidden (they are placed at the end when shown)
export const getDefaultPreferences = <T>(roleWidgets: RoleDashboardWidget<T>[]): DashboardWidgetPreference[] => {
    let x = 0
    let y = 0
    let rowHeight = 0
    return roleWidgets.map(({ widget, visible, x: fixedX, y: fixedY, w: customW, h: customH }) => {
        if (!visible) {
            return { id: widget.id, size: widget.defaultSize, visible: false }
        }
        const w = customW || widgetSizeGrid[widget.defaultSize].w
        const h = customH || widgetSizeGrid[widget.defaultSize].h
        // A role can fix where the card goes in its default layout
        if (fixedX !== undefined && fixedY !== undefined) {
            return { id: widget.id, size: widget.defaultSize, visible: true, x: fixedX, y: fixedY, w, h }
        }
        if (x + w > DASHBOARD_COLUMNS) {
            x = 0
            y += rowHeight
            rowHeight = 0
        }
        const preference: DashboardWidgetPreference = { id: widget.id, size: widget.defaultSize, visible: true, x, y, w, h }
        x += w
        rowHeight = Math.max(rowHeight, h)
        return preference
    })
}

// The saved choices are kept; cards the role no longer has are dropped and new cards are added
// (the default ones at the end of the grid, the others hidden)
const mergePreferences = <T>(
    roleWidgets: RoleDashboardWidget<T>[],
    saved?: DashboardWidgetPreference[]
): DashboardWidgetPreference[] => {
    if (!saved) {
        return getDefaultPreferences(roleWidgets)
    }
    const known = saved.filter(preference => roleWidgets.some(({ widget }) => widget.id === preference.id))
    const added = roleWidgets
        .filter(({ widget }) => !known.some(preference => preference.id === widget.id))
        .map(({ widget, visible }) => ({ id: widget.id, size: widget.defaultSize, visible }))
    return [...known, ...added]
}

// Only finite grid positions are saved (a card without position is placed at the end)
const toSavedPreference = ({ x, y, w, h, ...preference }: DashboardWidgetPreference): DashboardWidgetPreference => {
    const hasPosition = [x, y, w, h].every(value => Number.isFinite(value))
    return hasPosition ? { ...preference, x, y, w, h } : preference
}

// Home dashboard configuration of the current user for the current company (saved on the user, like the bookmarks)
export const useDashboardPreferences = <T>(roleWidgets: RoleDashboardWidget<T>[]) => {
    const { user } = useSystemState()
    const { companyId } = useSystemStateHooks()
    const { setUser } = useSystemStateActions()
    const getSystemActions = useSystemActions()

    const saved = companyId ? user?.dashboards?.[companyId] : undefined
    const preferences = useMemo(() => mergePreferences(roleWidgets, saved), [roleWidgets, saved])

    const savePreferences = async (newPreferences: DashboardWidgetPreference[]) => {
        if (!user || !companyId) return
        const snapshot = user
        const updatedUser = {
            ...user,
            dashboards: { ...user.dashboards, [companyId]: newPreferences.map(toSavedPreference) }
        }
        setUser(updatedUser)
        try {
            const { saveUser } = getSystemActions()
            await saveUser(updatedUser, false)
        } catch {
            setUser(snapshot)
            toast.error('Error al guardar la configuración del inicio. Intentá de nuevo.', {
                position: 'bottom-right',
                autoClose: 4000,
                hideProgressBar: true,
                theme: 'dark'
            })
        }
    }

    return {
        preferences,
        savePreferences
    }
}
